// Ported from obsidian-plugin/src/components/RetrievalCard.tsx.
// Obsidian adaptations:
//   - new Notice(...) → toast from sonner
//   - app.workspace.openLinkText(vault_path) → navigate('/sources/<cite_key>') via react-router
//   - Vault "Save to MalpediaRAG folder" export → download a .md blob via a synthetic <a download>
//   - InfoModal → simple toast explaining the card
//   - react-icons/bs BsDownload → inline unicode arrow
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  PipelineStep,
  PlanningEvent,
  RoutingEvent,
  StepDetail,
  GraphTraversalEvent,
  ProfileEvent,
  ScoreThreshold,
  SourceDoc,
} from '../../types';
import { ProgressPipeline, StepNode } from './ProgressPipeline';
import { LiveStatus } from './LiveStatus';

const STRATEGY_TOOLTIPS: Record<string, string> = {
  vector:                'Semantic search — finds passages by meaning similarity',
  bm25:                  'Keyword search — exact term matching for maximum recall',
  entity_lookup:         'Looks up all text chunks mentioning a specific named entity',
  temporal:              'Semantic search filtered and sorted by document date',
  contextual:            'Semantic search with keyword score boosting',
  hyde:                  'Hypothetical Document Embeddings — generates an imagined answer, then searches for real matches',
  summary:               'Searches document summaries first, then retrieves chunks from the best-matching reports',
  graph_path:            'Finds text chunks that co-mention two entities (graph traversal)',
  attribution:           'Finds which threat actors use a malware family (graph relationship)',
  ioc_pivot:             'Pivots from an indicator (IP, hash, domain) to related threats and entities',
  ioc_correlation:       'Finds entities that share indicators with a given entity',
  detection:             'Matches a list of indicators against the knowledge base to identify threats',
  comprehensive_profile: 'Full entity profile — combines attribution, IOCs, timeline, and semantic search',
  web_search:            'Live internet search for recent or emerging threats not yet in the local corpus',
};

const QUERY_TYPE_TOOLTIPS: Record<string, string> = {
  relationship:           'Connections or links between two named entities',
  activity_timeline:      'Recent activity, campaigns, or timeline of an entity',
  technique_context:      'Attack techniques, TTPs, or methods',
  entity_profile:         'Profile of a single malware family or threat actor',
  comparison:             'Side-by-side comparison of two or more entities',
  ioc_lookup:             'Lookup of a specific indicator (IP, hash, domain)',
  attribution:            'Who operates or deploys a malware family',
  ioc_pivot:              'Pivot from an indicator to connected threats',
  ioc_correlation:        'Entities sharing infrastructure or indicators',
  detection:              'Identify threats from a set of indicators',
  campaign_analysis:      'Reports mentioning multiple entities together',
  evolution:              'How an entity changed or developed over time',
  comprehensive_profile:  'Everything about a single entity — full deep-dive',
  general:                'Broad or conceptual query',
};

const SOURCE_TOOLTIPS: Record<string, string> = {
  heuristic: 'Matched a common pattern (entity lookup, IOC, temporal) — no LLM needed',
  llm:       'An LLM analyzed your query and designed a custom retrieval plan',
};

const DEPTH_TOOLTIPS: Record<string, string> = {
  narrow:     'Minimal retrieval — single item lookup (5 candidates)',
  normal:     'Focused analysis of 1-2 entities (10 candidates)',
  broad:      'Multi-entity or comparative queries (25 candidates)',
  exhaustive: 'Deep analysis across many documents (50 candidates)',
  mega:       'Maximum recall — "find all" enumeration (150 candidates)',
};

const MODE_TOOLTIPS: Record<string, string> = {
  auto: 'Analyzes your question and picks the best approach',
  vector: 'Finds passages by meaning similarity and keywords',
  local: 'Follows entity relationships from initial text matches to find connected intelligence',
  global: 'Looks up threat actors and malware families by name',
  drift: 'Explores outward from matches — extracts entities, then re-searches to find unexpected connections',
  planned: 'AI planner combines multiple search approaches for complex questions',
  llm_only: 'No document search — answers from conversation only',
};

const MODE_LABELS: Record<string, string> = {
  auto: 'Auto',
  vector: 'Semantic',
  local: 'Connected',
  global: 'Entity',
  drift: 'Discovery',
  planned: 'Planned',
  llm_only: 'Chat only',
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const cleanMsg = (msg: string) => msg.replace(/\.{2,}$/, '').trimEnd();

/* ── FlowSection ─────────────────────────────────────────────────────── */

function FlowSection({
  title,
  summary,
  variant,
  defaultOpen = false,
  children,
}: {
  title: string;
  summary: React.ReactNode;
  variant?: 'plan' | 'retrieve' | 'rerank' | 'generate' | 'error';
  defaultOpen?: boolean;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const hasBody = !!children;
  const cls = `malpedia-flow-section${variant ? ` malpedia-flow-section--${variant}` : ''}`;

  return (
    <div className={cls}>
      <button
        className="malpedia-flow-section-header"
        onClick={() => hasBody && setOpen((o) => !o)}
        style={{ cursor: hasBody ? 'pointer' : 'default' }}
      >
        <span className="malpedia-flow-section-title">{title}</span>
        <span className="malpedia-flow-section-summary">{summary}</span>
        {hasBody && (
          <span className="malpedia-flow-section-chevron">{open ? '▾' : '▸'}</span>
        )}
      </button>
      {open && children && (
        <div className="malpedia-flow-section-body">{children}</div>
      )}
    </div>
  );
}

/* ── PlanningSection ─────────────────────────────────────────────────── */

function PlanningSection({
  event,
  profileEvent,
}: {
  event: PlanningEvent;
  profileEvent?: ProfileEvent | null;
}) {
  const textParts: string[] = [];
  if (event.temporal_hint) textParts.push(event.temporal_hint);
  if (event.entities.length) textParts.push(`${event.entities.length} entities`);
  if (event.keywords.length) textParts.push(`${event.keywords.length} kw`);
  if (event.strategies.length) textParts.push(`${event.strategies.length} strategies`);

  const sourceLabel =
    event.source === 'heuristic'
      ? 'heuristic'
      : event.model
        ? `via ${event.model}`
        : event.source === 'llm'
          ? 'via LLM'
          : undefined;

  const sourceKey = event.source === 'heuristic' ? 'heuristic' : 'llm';

  const summary = (
    <>
      <span data-tooltip={QUERY_TYPE_TOOLTIPS[event.query_type]}>{event.query_type}</span>
      {textParts.length > 0 && ` · ${textParts.join(' · ')}`}
      {sourceLabel && <span className="malpedia-plan-source-badge" data-tooltip={SOURCE_TOOLTIPS[sourceKey]}>{sourceLabel}</span>}
      {event.fallback && (
        <span
          className="malpedia-retrieval-fallback-badge"
          title={event.reasoning ?? undefined}
        >
          fallback
        </span>
      )}
    </>
  );

  const variant = event.fallback ? ('error' as const) : ('plan' as const);

  // Drop keywords that duplicate an entity chip (case-insensitive).
  // The backend planner already dedupes, but guard here so a stale
  // API server can't render redundant chips.
  const entityLowers = new Set(
    event.entities.map((e) => e.trim().toLowerCase()),
  );
  const dedupedKeywords = event.keywords.filter(
    (k) => !entityLowers.has(k.trim().toLowerCase()),
  );

  return (
    <FlowSection title="Plan" summary={summary} variant={variant}>
      {(event.entities.length > 0 || dedupedKeywords.length > 0) && (
        <div className="malpedia-retrieval-chips">
          {event.entities.map((e, i) => (
            <span key={`e-${i}`} className="malpedia-retrieval-chip">
              {e}
            </span>
          ))}
          {dedupedKeywords.map((k, i) => (
            <span key={`k-${i}`} className="malpedia-keyword-chip">
              {k}
            </span>
          ))}
        </div>
      )}

      {event.strategies.length > 0 && (
        <div className="malpedia-retrieval-strategies">
          {event.strategies.map((s, i) => (
            <div key={i} className="malpedia-strategy-row">
              <span className="malpedia-strategy-name" data-tooltip={STRATEGY_TOOLTIPS[s.name]}>{s.name}</span>
              <div className="malpedia-strategy-bar-wrap">
                <div
                  className="malpedia-strategy-bar"
                  style={{ width: `${(s.weight * 100).toFixed(0)}%` }}
                />
              </div>
              <span className="malpedia-strategy-pct">{(s.weight * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      )}

      {profileEvent && <ProfileBadges event={profileEvent} />}

      {event.reasoning && !event.fallback && (
        <div className="malpedia-reasoning-block">{event.reasoning}</div>
      )}
      {event.fallback && event.reasoning && (
        <div className="malpedia-reasoning-block malpedia-reasoning-block--error">
          {event.reasoning}
        </div>
      )}
    </FlowSection>
  );
}

/* ── ProfileBadges ───────────────────────────────────────────────────── */

function ProfileBadges({ event }: { event: ProfileEvent }) {
  const DEPTH_NAMES: Record<string, string> = {
    auto: 'Auto', narrow: 'Quick', normal: 'Balanced', broad: 'Thorough',
    manual: 'Custom',
    exhaustive: 'Deep', mega: 'Sweep',
  };
  return (
    <div className="malpedia-profile-badges">
      <span className="malpedia-profile-chip" data-tooltip={DEPTH_TOOLTIPS[event.depth]}>{DEPTH_NAMES[event.depth] ?? event.depth}</span>
      <span className="malpedia-profile-chip">Candidates {event.top_k}</span>
      <span className="malpedia-profile-chip">Merge {event.fusion_cap}</span>
      <span className="malpedia-profile-chip">Context {event.top_sources}</span>
    </div>
  );
}

/* ── Elapsed timer hook ─────────────────────────────────────────────── */

function useElapsedTimer(startTs: number | undefined, active: boolean): string {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!active || startTs == null) return;
    const id = setInterval(() => tick((n) => n + 1), 100);
    return () => clearInterval(id);
  }, [active, startTs]);

  if (startTs == null) return '';
  const elapsed = (Date.now() / 1000 - startTs);
  if (elapsed < 0) return '';
  return `${elapsed.toFixed(1)}s`;
}

/** Format a static duration between two timestamps. */
function formatDuration(fromTs?: number, toTs?: number): string {
  if (fromTs == null || toTs == null) return '';
  const d = toTs - fromTs;
  if (d < 0) return '';
  return `${d.toFixed(1)}s`;
}

/* ── ActivityTimeline (live waiting state) ────────────────────────────── */

const STEP_VARIANT: Record<string, string> = {
  planning: 'plan',
  retrieving: 'retrieve',
  prefilter: 'retrieve',
  iterative: 'retrieve',
  per_doc_cap: 'retrieve',
  neighbor_expand: 'retrieve',
  full_doc_expand: 'retrieve',
  graph_expanding: 'retrieve',
  reranking: 'rerank',
  score_cutoff: 'rerank',
  context_budget: 'rerank',
  generating: 'generate',
};

interface ActivityTimelineProps {
  stepDetails: StepDetail[];
  progressStep: PipelineStep | string | null | undefined;
  routingEvent?: RoutingEvent | null;
}

function ActivityTimelineEntry({
  detail,
  isActive,
  nextTs,
}: {
  detail: StepDetail;
  isActive: boolean;
  nextTs?: number;
}) {
  const variant = STEP_VARIANT[detail.step] ?? '';
  const dotCls = [
    'malpedia-timeline-dot',
    variant && `malpedia-timeline-dot--${variant}`,
    isActive ? 'malpedia-timeline-dot--active' : 'malpedia-timeline-dot--done',
  ]
    .filter(Boolean)
    .join(' ');

  const elapsed = useElapsedTimer(detail.ts, isActive);
  const staticDuration = !isActive ? formatDuration(detail.ts, nextTs) : '';

  const durationText = isActive ? elapsed : staticDuration;
  const durationCls = isActive
    ? 'malpedia-timeline-duration malpedia-timeline-duration--active'
    : 'malpedia-timeline-duration';

  return (
    <div className="malpedia-timeline-entry">
      <span className={dotCls}>
        <StepNode status={isActive ? 'active' : 'done'} />
      </span>
      <span className="malpedia-timeline-label">{capitalize(detail.step.replace(/_/g, ' '))}</span>
      <span className="malpedia-timeline-msg">{cleanMsg(detail.message)}</span>
      {detail.count != null && (
        <span className="malpedia-timeline-count">{detail.count}</span>
      )}
      {durationText && <span className={durationCls}>{durationText}</span>}
    </div>
  );
}

function ActivityTimeline({ stepDetails, progressStep, routingEvent }: ActivityTimelineProps) {
  return (
    <div className="malpedia-activity-timeline">
      {routingEvent && (
        <div className="malpedia-timeline-routing">
          <span
            className="malpedia-retrieval-bar-mode"
            title={MODE_TOOLTIPS[routingEvent.mode] ?? routingEvent.mode}
          >
            {MODE_LABELS[routingEvent.mode] ?? routingEvent.mode}
            {routingEvent.confidence < 1.0 && (
              <span className="malpedia-retrieval-bar-confidence">
                {' '}{(routingEvent.confidence * 100).toFixed(0)}%
              </span>
            )}
          </span>
          {routingEvent.reasoning && (
            <span className="malpedia-timeline-msg">{routingEvent.reasoning}</span>
          )}
        </div>
      )}

      {stepDetails.length === 0 && !routingEvent && (
        <div className="malpedia-timeline-entry">
          <span className="malpedia-timeline-dot malpedia-timeline-dot--active">⟳</span>
          <span className="malpedia-timeline-shimmer">Starting…</span>
        </div>
      )}

      {stepDetails.map((d, i) => {
        const isLast = i === stepDetails.length - 1;
        const isActive = isLast && d.step === progressStep;
        const nextTs = !isActive && i < stepDetails.length - 1 ? stepDetails[i + 1].ts : undefined;
        return (
          <ActivityTimelineEntry
            key={i}
            detail={d}
            isActive={isActive}
            nextTs={nextTs}
          />
        );
      })}
    </div>
  );
}

/* ── SteppedFlow ─────────────────────────────────────────────────────── */

function SteppedFlow({ steps }: { steps: StepDetail[] }) {
  if (steps.length === 0) return null;

  return (
    <div className="malpedia-stepped-flow">
      {steps.map((d, i) => {
        const text = cleanMsg(d.message);
        const nextTs = i < steps.length - 1 ? steps[i + 1].ts : undefined;
        const duration = formatDuration(d.ts, nextTs);
        return (
          <React.Fragment key={i}>
            {i > 0 && <div className="malpedia-stepped-connector" />}
            <div className="malpedia-stepped-step">
              <span className="malpedia-stepped-label">{capitalize(d.step)}</span>
              {d.count != null && <span className="malpedia-stepped-count">{d.count}</span>}
              <span className="malpedia-stepped-msg">{text}</span>
              {duration && <span className="malpedia-stepped-duration">{duration}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Markdown export helper ──────────────────────────────────────────── */

function buildScoreTableMd(
  sources: SourceDoc[] | undefined,
  scoreThreshold: ScoreThreshold | null | undefined,
): string {
  if (!sources?.length && !scoreThreshold?.excluded_sources?.length) return '';

  const included = (sources || []).map((s, i) => ({
    rank: i + 1,
    cite_key: s.cite_key,
    score: s.score,
    title: s.title ?? '',
    status: 'included',
  }));
  const excluded = (scoreThreshold?.excluded_sources || []).map((s, i) => ({
    rank: included.length + i + 1,
    cite_key: s.cite_key,
    score: s.score,
    title: s.title ?? '',
    status: 'excluded',
  }));
  const rows = [...included, ...excluded];
  if (rows.length === 0) return '';

  let md = `## Source Scores\n\n`;
  md += `| # | Cite Key | Score | Title | Status |\n`;
  md += `|---|----------|-------|-------|--------|\n`;
  for (const r of rows) {
    md += `| ${r.rank} | ${r.cite_key} | ${r.score.toFixed(3)} | ${r.title} | ${r.status} |\n`;
  }
  md += '\n';
  return md;
}

export function buildRetrievalMd(
  completedSteps: PipelineStep[] | undefined,
  stepDetails: StepDetail[] | undefined,
  planningEvent: PlanningEvent | null | undefined,
  routingEvent: RoutingEvent | null | undefined,
  profileEvent: ProfileEvent | null | undefined,
  sources?: SourceDoc[],
  scoreThreshold?: ScoreThreshold | null,
): string {
  let md = '';

  if (routingEvent) {
    md += `## Routing\n\n`;
    md += `**Mode:** ${routingEvent.mode} · ${(routingEvent.confidence * 100).toFixed(0)}% confidence\n\n`;
    if (routingEvent.reasoning) md += `> ${routingEvent.reasoning}\n\n`;
  }

  if (planningEvent) {
    const pe = planningEvent;
    md += `## Planning\n\n`;
    md += `**Query type:** ${pe.query_type}`;
    if (pe.temporal_hint) md += ` · **Temporal:** ${pe.temporal_hint}`;
    md += '\n';
    if (pe.entities.length > 0) md += `**Entities:** ${pe.entities.join(', ')}\n`;
    if (pe.keywords.length > 0) md += `**Keywords:** ${pe.keywords.join(', ')}\n`;
    if (pe.strategies.length > 0) {
      md += `\n### Strategies\n\n| Strategy | Weight |\n|---|---|\n`;
      for (const s of pe.strategies) {
        md += `| ${s.name} | ${(s.weight * 100).toFixed(0)}% |\n`;
      }
    }
    if (pe.reasoning) {
      md += `\n### ${pe.fallback ? 'Error' : 'Reasoning'}\n\n${pe.reasoning}\n`;
    }
    md += '\n';
  }

  if (profileEvent) {
    const depthName: Record<string, string> = { auto: 'Auto', narrow: 'Quick', normal: 'Balanced', broad: 'Thorough', exhaustive: 'Deep', mega: 'Sweep', manual: 'Custom' };
    md += `## Profile\n\n`;
    md += `**Depth:** ${depthName[profileEvent.depth] ?? profileEvent.depth} · **Candidates:** ${profileEvent.top_k} · **Merge limit:** ${profileEvent.fusion_cap} · **Context size:** ${profileEvent.top_sources}\n\n`;
  }

  if (stepDetails?.length) {
    const seen = new Map<string, StepDetail>();
    for (const d of stepDetails) seen.set(d.step, d);
    const deduped = [...seen.values()].filter((d) => !(d.step === 'planning' && planningEvent));
    md += `## Steps\n\n`;
    for (const d of deduped) {
      const msg = cleanMsg(d.message);
      md += `- **${capitalize(d.step)}:** ${msg}${d.count != null ? ` _(${d.count})_` : ''}\n`;
    }
    md += '\n';
  } else if (completedSteps?.length) {
    md += `**Pipeline:** ${completedSteps.join(' → ')}\n\n`;
  }

  md += buildScoreTableMd(sources, scoreThreshold);

  return md;
}

/** Trigger a client-side download of a markdown blob. */
function downloadMarkdown(filename: string, md: string): void {
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── RetrievalCard ───────────────────────────────────────────────────── */

interface RetrievalCardProps {
  streaming?: boolean;
  hasContent?: boolean;
  progressStep?: PipelineStep | null;
  progressMessage?: string;
  progressCount?: number;
  completedSteps?: PipelineStep[];
  stepDetails?: StepDetail[];
  planningEvent?: PlanningEvent | null;
  routingEvent?: RoutingEvent | null;
  graphTraversalEvent?: GraphTraversalEvent | null;
  profileEvent?: ProfileEvent | null;
  scoreThreshold?: ScoreThreshold | null;
  sources?: SourceDoc[];
  planningTokens?: string;
}

export function RetrievalCard({
  streaming,
  hasContent,
  progressStep,
  progressMessage,
  progressCount,
  completedSteps,
  stepDetails,
  planningEvent,
  routingEvent,
  profileEvent,
  scoreThreshold,
  sources,
  planningTokens,
}: RetrievalCardProps) {
  const isThinking = !!streaming && !hasContent;
  const [open, setOpen] = useState(false);

  // Auto-open when thinking starts, auto-collapse when content arrives or streaming ends
  const prevStreamingRef = useRef(streaming);
  const prevThinkingRef = useRef(isThinking);
  useEffect(() => {
    // Auto-open when thinking begins
    if (isThinking && !prevThinkingRef.current) {
      setOpen(true);
    }
    // Auto-collapse when first token arrives (thinking → not thinking)
    if (!isThinking && prevThinkingRef.current) {
      setOpen(false);
    }
    // Auto-collapse when streaming ends entirely
    if (prevStreamingRef.current && !streaming) {
      setOpen(false);
    }
    prevStreamingRef.current = streaming;
    prevThinkingRef.current = isThinking;
  }, [streaming, isThinking]);

  const chainSteps = useMemo(() => {
    const seen = new Map<string, StepDetail>();
    for (const d of stepDetails ?? []) seen.set(d.step, d);
    return [...seen.values()].filter((d) => !(d.step === 'planning' && planningEvent));
  }, [stepDetails, planningEvent]);

  const retrieveSteps = useMemo(
    () => chainSteps.filter((d) => STEP_VARIANT[d.step] === 'retrieve'),
    [chainSteps],
  );
  const rerankSteps = useMemo(
    () => chainSteps.filter((d) => STEP_VARIANT[d.step] === 'rerank'),
    [chainSteps],
  );
  const generateSteps = useMemo(
    () => chainSteps.filter((d) => STEP_VARIANT[d.step] === 'generate'),
    [chainSteps],
  );

  const usedWeb = useMemo(() => {
    if (stepDetails?.some((d) => d.step.includes('web_search'))) return true;
    if (sources?.some((s) => s.cite_key.startsWith('web:') || s.retrieved_by === 'web_search')) return true;
    return false;
  }, [stepDetails, sources]);

  const bucketSummary = (steps: StepDetail[]): string => {
    const counts = steps.filter((d) => d.count != null).map((d) => d.count as number);
    if (counts.length === 0) {
      return steps.length > 0 ? `${steps.length} steps` : '';
    }
    if (counts.length === 1) return `${counts[0]} chunks`;
    return `${counts[0]} → ${counts[counts.length - 1]} chunks`;
  };

  const retrieveSectionSummary = bucketSummary(retrieveSteps);

  const rerankSectionSummary = useMemo(() => {
    const parts: string[] = [];
    const counts = rerankSteps.filter((d) => d.count != null).map((d) => d.count as number);
    if (counts.length > 0) parts.push(`${counts[counts.length - 1]} kept`);
    else if (rerankSteps.length > 0) parts.push(`${rerankSteps.length} steps`);

    if (scoreThreshold?.min_score != null) {
      parts.push(`min ${scoreThreshold.min_score.toFixed(2)}`);
    }
    return parts.join(' · ');
  }, [rerankSteps, scoreThreshold]);

  const generateSectionSummary = useMemo(() => {
    if (generateSteps.length === 0) return '';
    const last = generateSteps[generateSteps.length - 1];
    return cleanMsg(last.message);
  }, [generateSteps]);

  const handleExport = useCallback(() => {
    try {
      const now = new Date();
      const ts = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const dateStr = now.toISOString().slice(0, 16).replace('T', ' ');
      const fileName = `Retrieval-${ts}.md`;
      const md =
        `# Retrieval Record — ${dateStr}\n\n` +
        buildRetrievalMd(
          completedSteps,
          stepDetails,
          planningEvent,
          routingEvent,
          profileEvent,
          undefined,
          scoreThreshold,
        );
      downloadMarkdown(fileName, md);
      toast.success('Retrieval record downloaded');
    } catch (e) {
      toast.error(`Failed to export retrieval: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [completedSteps, stepDetails, planningEvent, routingEvent, profileEvent, scoreThreshold]);

  const hasRerankBlock =
    rerankSteps.length > 0 ||
    !!(scoreThreshold && (scoreThreshold.min_score != null || scoreThreshold.cut_score != null));
  const hasDetails =
    !!planningEvent ||
    retrieveSteps.length > 0 ||
    hasRerankBlock ||
    generateSteps.length > 0;
  const isLlmOnly = routingEvent?.mode === 'llm_only';

  // Before the first `progress` event lands, fall back to 'planning' so the
  // pipeline bar and live-status row both have a sane initial phase to render.
  const effectiveStep: PipelineStep | null | undefined =
    progressStep ?? (streaming && !isLlmOnly ? 'planning' : null);

  // Keep the bar mounted as a permanent breadcrumb for any finished assistant
  // turn that carries any retrieval-derived signal.
  const shouldRender =
    isThinking ||
    streaming ||
    !!routingEvent ||
    (completedSteps != null && completedSteps.length > 0) ||
    (stepDetails != null && stepDetails.length > 0) ||
    !!planningEvent ||
    !!profileEvent ||
    !!scoreThreshold ||
    (sources != null && sources.length > 0) ||
    !!hasContent;
  if (!shouldRender) return null;

  return (
    <div className="malpedia-retrieval-card">
      {/* Bar — ALWAYS visible, now at top */}
      <button
        className={`malpedia-retrieval-card-bar${open && hasDetails && !isLlmOnly ? ' open' : ''}`}
        onClick={() => hasDetails && !isLlmOnly && setOpen((o) => !o)}
        style={{ cursor: hasDetails && !isLlmOnly ? 'pointer' : 'default' }}
        title={hasDetails && !isLlmOnly ? (open ? 'Collapse retrieval details' : 'Expand retrieval details') : undefined}
      >
        {routingEvent && routingEvent.mode !== 'planned' && (
          <span
            className="malpedia-retrieval-bar-mode"
            title={MODE_TOOLTIPS[routingEvent.mode] ?? routingEvent.mode}
          >
            {MODE_LABELS[routingEvent.mode] ?? routingEvent.mode}
            {routingEvent.confidence < 1.0 && (
              <span className="malpedia-retrieval-bar-confidence">
                {' '}
                {(routingEvent.confidence * 100).toFixed(0)}%
              </span>
            )}
          </span>
        )}

        {isLlmOnly && (
          <span className="malpedia-retrieval-bar-summary" data-tooltip="No document retrieval was performed for this turn">
            Chat only — no retrieval
          </span>
        )}

        {!isLlmOnly && usedWeb && (
          <span className="malpedia-retrieval-bar-web" title="Web search was used">
            🌐 web
          </span>
        )}

        {/* ProgressPipeline — always visible in bar (suppressed for llm_only) */}
        {!isLlmOnly && (streaming || (completedSteps && completedSteps.length > 0)) && (
          <ProgressPipeline
            step={effectiveStep}
            completedSteps={completedSteps}
            stepDetails={stepDetails}
          />
        )}

        <span style={{ flex: 1 }} />
        {hasDetails && !isLlmOnly && (
          <span className="malpedia-retrieval-card-chevron">{open ? '▲' : '▼'}</span>
        )}
        {!streaming && (
          <span
            className="malpedia-retrieval-card-export"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleExport();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                handleExport();
              }
            }}
            title="Download retrieval record"
            aria-label="Download retrieval record"
          >
            ⬇
          </span>
        )}
      </button>

      {/* Live status — single-line "Doing X…" + streaming planner tail.
          Sits between the pipeline bar and the (optional) details panel, so
          it's visible whether or not the card is expanded. */}
      {streaming && !isLlmOnly && (
        <LiveStatus
          progressStep={effectiveStep}
          progressMessage={progressMessage}
          planningTokens={planningTokens}
        />
      )}

      {/* Details — timeline while thinking, flow sections after */}
      {open && isThinking && (
        <div className="malpedia-retrieval-card-details">
          <ActivityTimeline
            stepDetails={stepDetails ?? []}
            progressStep={progressStep}
            routingEvent={routingEvent}
          />
        </div>
      )}
      {open && !isThinking && hasDetails && (
        <div className="malpedia-retrieval-card-details">
          {planningEvent && (
            <PlanningSection event={planningEvent} profileEvent={profileEvent} />
          )}

          {retrieveSteps.length > 0 && (
            <FlowSection title="Retrieve" summary={retrieveSectionSummary} variant="retrieve" defaultOpen>
              <SteppedFlow steps={retrieveSteps} />
            </FlowSection>
          )}

          {hasRerankBlock && (
            <FlowSection title="Rerank" summary={rerankSectionSummary} variant="rerank" defaultOpen>
              {rerankSteps.length > 0 && <SteppedFlow steps={rerankSteps} />}
              {scoreThreshold &&
                (scoreThreshold.min_score != null || scoreThreshold.cut_score != null) && (
                  <div className="malpedia-score-threshold">
                    <div className="malpedia-score-threshold-row">
                      {scoreThreshold.min_score != null && (
                        <span className="malpedia-score-threshold-item">
                          <span className="malpedia-score-threshold-label">weakest kept</span>
                          <span className="malpedia-score-threshold-value">
                            {scoreThreshold.min_score.toFixed(2)}
                          </span>
                        </span>
                      )}
                      {scoreThreshold.cut_score != null && (
                        <span className="malpedia-score-threshold-item malpedia-score-threshold-item--cut">
                          <span className="malpedia-score-threshold-label">first dropped</span>
                          <span className="malpedia-score-threshold-value">
                            {scoreThreshold.cut_score.toFixed(2)}
                          </span>
                        </span>
                      )}
                      {scoreThreshold.cutoff_dropped > 0 && (
                        <span className="malpedia-score-threshold-item">
                          <span className="malpedia-score-threshold-label">dropped (low score)</span>
                          <span className="malpedia-score-threshold-value">
                            {scoreThreshold.cutoff_dropped}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                )}
            </FlowSection>
          )}

          {generateSteps.length > 0 && (
            <FlowSection title="Generate" summary={generateSectionSummary} variant="generate" defaultOpen>
              <SteppedFlow steps={generateSteps} />
            </FlowSection>
          )}
        </div>
      )}
    </div>
  );
}
